import { fetchRequestPages } from "./requestQueue";
import { configuredRequestActor } from "./configuredRequestActor";

export async function loadRequestQueue(baseUrl, user, signal) {
  const role = String(user.role || "").trim().toLowerCase();
  const meId = user._id || "";
  const meUsername = user.username || "";
  const isAFD = role === "afd";
  const isInventoryAdmin = role === "inventory admin";
  const isRegionalDirector = role === "regional director";
  const isSuperAdmin = ["super admin", "admin", "superadmin"].includes(role);
  const matchesIdentity = value => value != null && (
    (meId && String(value) === String(meId)) ||
    (meUsername && String(value) === meUsername)
  );
  const matchesDistributionRecipient = item =>
    [item.distributedto_is, item.distributedto].some(matchesIdentity);
  const matchesPropertyRecipient = item => [
    item.issued_to_id, item.issued_to, item.current_holder_id, item.current_holder,
    item.transfered_to_id, item.transferedto_id, item.transferred_to_id,
    item.transfered_to_is, item.transferedto_is, item.transferred_to_is,
    item.transfered_to, item.transferedto, item.transferred_to,
  ].some(matchesIdentity);
  const isOtherAgencyTransfer = (inv) => {
    const otherAgencyTarget =
      inv?.transfer_target ||
      inv?.transfer_to ||
      inv?.transfer_to_agency ||
      inv?.transfered_to_agency ||
      inv?.agency_to ||
      inv?.receiving_agency ||
      "";

    const transferTypeRaw =
      (inv?.transfer_type || inv?.transferType || inv?.transfer_mode || "") +
      "";

    return (
      /\bother\s*agency\b/i.test(transferTypeRaw) ||
      (!!otherAgencyTarget && String(otherAgencyTarget).trim() !== "")
    );
  };

  const isDisposalRecord = (inv) => {
    const s = (inv?.status || "").toLowerCase().trim();
    return (
      s === "for disposal" ||
      s === "disposed" ||
      !!inv?.disposal_reason ||
      !!inv?.disposal_notes
    );
  };
  const isReturnToInventoryRecord = (inv) => {
    const s = (inv?.status || "").toLowerCase().trim();
    return s.includes("return to inventory");
  };
  const isIssueRecord = (inv) => {
    const s = (inv?.status || "").toLowerCase().trim();
    return s.includes("for issue");
  };

  const hasTransferIntent = (inv) =>
    !!(
      inv?.transfered_to_id ||
      inv?.transferedto_id ||
      inv?.transferred_to_id ||
      inv?.transfered_to ||
      inv?.transferedto ||
      inv?.transferred_to ||
      inv?.transfer_type ||
      inv?.transferType ||
      inv?.transfer_mode
    );

  const isTransferStage = (inv) => {
    const rs = (inv?.requeststatus || "").toLowerCase().trim();
    return rs === "for approval" || rs === "for release" || rs === "to receive";
  };

  const normalizeStatus = (value) => String(value || "").toLowerCase().trim();

  const needsAttentionDistribution = (dist) => {
    const rs = normalizeStatus(dist?.requeststatus);
    const st = normalizeStatus(dist?.status);
    if ([rs, st].some((status) => status === "cancelled" || status === "canceled")) return false;
    const requestType = normalizeStatus(dist?.request_type);
    const isDisposal =
      requestType === "disposal" || st === "for disposal" || st === "disposed";

    if (rs === "received" || rs === "declined") return false;
    if (isDisposal && st === "disposed") return false;

    if (rs) {
      return (
        rs === "for checking" ||
        rs.startsWith("for approval") ||
        rs === "for release" ||
        rs === "approved" ||
        rs === "to receive"
      );
    }

    if (isDisposal) return st === "for disposal";

    return st === "pending" || st === "approved";
  };

  const needsAttentionProperty = (inv) => {
    const rs = normalizeStatus(inv?.requeststatus);
    const st = normalizeStatus(inv?.status);
    if ([rs, st].some((status) => status === "cancelled" || status === "canceled")) return false;
    const disposal = isDisposalRecord(inv);
    const issue = isIssueRecord(inv);
    const returnToInventory = isReturnToInventoryRecord(inv);
    const transferIntent = hasTransferIntent(inv);
    const transferStage = isTransferStage(inv);
    const otherAgency = isOtherAgencyTransfer(inv);

    if (rs === "declined" || rs === "received") return false;
    if (st === "disposed") return false;

    if (disposal) return rs.startsWith("for approval") || st === "for disposal";
    if (issue) return rs.startsWith("for approval") || st.includes("for issue");
    if (returnToInventory)
      return rs === "to receive" || st.includes("return to inventory");

    if (transferIntent) {
      if (transferStage) return true;
      if (st === "for transfer") return true;
      if (rs === "for release" || rs === "to receive") return true;
    }

    if (otherAgency) return false;

    return (
      rs.startsWith("for approval") ||
      rs === "for release" ||
      rs === "to receive"
    );
  };

        const scopeItems = (allItems, onlyPendingOfficeSupply) => {
          const pending = allItems.filter(
            onlyPendingOfficeSupply ? needsAttentionDistribution : needsAttentionProperty
          );
          if (isSuperAdmin) return pending;
          if (onlyPendingOfficeSupply) {
            return pending.filter((item) => {
              const configured = configuredRequestActor(item, true, settings.document_signatories);
              if (configured) return String(configured) === String(meId);
              const stage = normalizeStatus(item.requeststatus);
              if (isInventoryAdmin) return stage === "for checking";
              if (isAFD) return stage === "for approval";
              return stage === "approved" && matchesDistributionRecipient(item);
            });
          }
          return pending.filter((item) => {
            const configured = configuredRequestActor(item, false, settings.document_signatories);
            if (configured) return String(configured) === String(meId);
            const stage = normalizeStatus(item.requeststatus);
            const disposal = isDisposalRecord(item);
            const issue = isIssueRecord(item);
            const returning = isReturnToInventoryRecord(item);
            if (isAFD) return disposal && stage === "for approval";
            if (isRegionalDirector) return !disposal && !issue && stage === "for approval";
            if (isInventoryAdmin) {
              return !disposal && (
                (issue && stage === "for approval") ||
                (returning && stage === "to receive") ||
                (!issue && !returning && stage === "for release")
              );
            }
            return !disposal && !returning && !isOtherAgencyTransfer(item) &&
              (stage === "to receive" || stage === "") && matchesPropertyRecipient(item);
          });
        };
        const propertyParams = new URLSearchParams({ pendingOnly: "true" });
        const supplyParams = new URLSearchParams({ pendingOnly: "true" });
  const [properties, supplies, settings] = await Promise.all([
    fetchRequestPages(`${baseUrl}/requests/property`, propertyParams, signal),
    fetchRequestPages(`${baseUrl}/distribute/distributions`, supplyParams, signal),
    fetch(`${baseUrl}/settings`, { credentials: "include", signal }).then(async response => {
      if (!response.ok) throw new Error("Unable to load request signatories.");
      return response.json();
    }),
  ]);
  return { properties: scopeItems(properties, false), supplies: scopeItems(supplies, true) };
}
